import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

// Pages
import Home from '../pages/home/Home';
import Login from '../pages/auth/Login';
import Dashboard from '../pages/dashboard/Dashboard';
import EmissorasPage from '../pages/emissoras/EmissorasPage';
import ClientesPage from '../pages/clientes/ClientesPage';
import AnunciosPage from '../pages/anuncios/AnunciosPage';
import MarcasPage from '../pages/marcas/MarcasPage';
import AgenciasPage from '../pages/agencias/AgenciasPage';
import AnunciantesPage from '../pages/anunciantes/AnunciantesPage';
import CategoriasPage from '../pages/categorias/CategoriasPage';
import GravacoesPage from '../pages/gravacoes/GravacoesPage';
import RegistrosPage from '../pages/registros/RegistrosPage';
import RegistroDetailPage from '../pages/registros/RegistroDetailPage';
import ItensPage from '../pages/itens/ItensPage';
import ProgramasPage from '../pages/programas/ProgramasPage';
import SetoresPage from '../pages/setores/SetoresPage';
import RelatoriosPage from '../pages/relatorios/RelatoriosPage';
import UsersPage from '../pages/users/UsersPage';

// Interface User
interface User {
  id: number;
  nome: string;
  email: string;
  roles?: string[];
  is_admin: boolean;
}

// Lista de rotas que apenas o admin pode acessar
const ADMIN_ONLY_ROUTES = [
  '/emissoras',
  '/clientes',
  '/anuncios',
  '/marcas',
  '/agencias',
  '/anunciantes',
  '/categorias',
  '/gravacoes',
  '/registros',
  '/itens',
  '/programas',
  '/setores',
  '/users'
];

// Rotas públicas - usuários não precisam estar autenticados
const PUBLIC_ROUTES = ['/', '/login'];

// Rotas que todos os usuários autenticados podem acessar
const USER_ROUTES = ['/relatorios', '/dashboard'];

// Loading Component
const LoadingScreen: React.FC = () => (
  <Box 
    display="flex" 
    flexDirection="column"
    justifyContent="center" 
    alignItems="center" 
    height="100vh"
    bgcolor="#f5f5f5"
  >
    <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
    <Typography variant="h6" color="textSecondary">
      Carregando aplicação...
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
      Por favor, aguarde enquanto verificamos seus dados.
    </Typography>
  </Box>
);

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children,
  requireAdmin = false
}) => {
  const { signed, loading, user } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Cast user to our interface
  const typedUser = user as User | null;
  
  // Verificar se a rota atual requer privilégios de admin
  const isAdminRoute = ADMIN_ONLY_ROUTES.some(route => 
    currentPath === route || currentPath.startsWith(`${route}/`)
  );
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  // Se não estiver autenticado, redirecionar para login
  if (!signed) {
    return <Navigate to="/login" replace />;
  }
  
  // Debug: Verificar se informações do usuário estão chegando corretamente
  console.log('User in ProtectedRoute:', typedUser);
  console.log('is_admin value:', typedUser?.is_admin);
  console.log('Current path:', currentPath);
  console.log('Is admin route:', isAdminRoute);
  
  // Se for uma rota de admin, mas o usuário não é admin, redirecionar para relatórios
  if (isAdminRoute && typedUser?.is_admin !== true) {
    console.log('Redirecionando usuário não-admin da rota restrita:', currentPath);
    return <Navigate to="/relatorios" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/emissoras" 
          element={
            <ProtectedRoute requireAdmin>
              <EmissorasPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/clientes" 
          element={
            <ProtectedRoute requireAdmin>
              <ClientesPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/anuncios" 
          element={
            <ProtectedRoute requireAdmin>
              <AnunciosPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/marcas" 
          element={
            <ProtectedRoute requireAdmin>
              <MarcasPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/agencias" 
          element={
            <ProtectedRoute requireAdmin>
              <AgenciasPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/anunciantes" 
          element={
            <ProtectedRoute requireAdmin>
              <AnunciantesPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/categorias" 
          element={
            <ProtectedRoute requireAdmin>
              <CategoriasPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/gravacoes" 
          element={
            <ProtectedRoute requireAdmin>
              <GravacoesPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/registros" 
          element={
            <ProtectedRoute requireAdmin>
              <RegistrosPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Deve vir ANTES de /registros/:id para não ser capturada como id="novo" */}
        <Route
          path="/registros/novo"
          element={
            <ProtectedRoute requireAdmin>
              <RegistrosPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/registros/:id"
          element={
            <ProtectedRoute requireAdmin>
              <RegistroDetailPage />
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/itens" 
          element={
            <ProtectedRoute requireAdmin>
              <ItensPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/programas" 
          element={
            <ProtectedRoute requireAdmin>
              <ProgramasPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/setores" 
          element={
            <ProtectedRoute requireAdmin>
              <SetoresPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/relatorios" 
          element={
            <ProtectedRoute>
              <RelatoriosPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/users" 
          element={
            <ProtectedRoute requireAdmin>
              <UsersPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Não precisamos mais deste redirecionamento, já que temos a Home page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes; 